import React, { useState, useEffect } from 'react';
import { Plus, Edit, AlertCircle, Save, X, Server, Globe, Lock, Check, XCircle } from 'lucide-react';
import { api } from '../services/api';
import { AdminLoading } from './admin/AdminLoading';
import { AdminPageHeader } from './admin/AdminPageHeader';
import { AdminAddButton } from './admin/AdminAddButton';
import { useAdminToast } from './admin/AdminToast';
import { useConfirmDialog } from './admin/ConfirmDialog';
import { AdminActionButton, AdminRowActions } from './admin/AdminActionButton';

const NodeManagement = () => {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { showSuccess, toastElement } = useAdminToast();
  const { confirm, confirmDialog } = useConfirmDialog();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    ztimeBasicUrl: '',
    datasyncBasicUrl: '',
    lisabackendBasicUrl: '',
    loginUsername: '',
    loginPassword: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadNodes();
  }, []);

  const loadNodes = async () => {
    setError('');
    try {
      const response = await api.get('/admin/nodes');
      setNodes(response.data);
    } catch (error) {
      console.error('Error loading nodes:', error);
      setError('Failed to load nodes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNode = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.post('/admin/nodes', formData);
      await loadNodes();
      setShowCreateForm(false);
      resetForm();
      showSuccess(`Node "${formData.name}" created`);
    } catch (error) {
      console.error('Error creating node:', error);
      setError(error.response?.data?.message || 'Failed to create node');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateNode = async (e) => {
    e.preventDefault();
    if (!editingNode) return;

    setSubmitting(true);
    setError('');
    try {
      await api.put(`/admin/nodes/${editingNode.id}`, formData);
      await loadNodes();
      setEditingNode(null);
      setShowCreateForm(false);
      resetForm();
      showSuccess(`Node "${formData.name}" updated`);
    } catch (error) {
      console.error('Error updating node:', error);
      setError(error.response?.data?.message || 'Failed to update node');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNode = async (id) => {
    const node = nodes.find((n) => n.id === id);
    const label = node ? node.name : 'this node';
    const confirmed = await confirm({
      title: `Delete node "${label}"?`,
      message: `"${label}" will be permanently deleted. Office mappings that use this node may stop syncing.`,
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;

    setError('');
    try {
      await api.delete(`/admin/nodes/${id}`);
      await loadNodes();
      showSuccess(`Node "${label}" deleted`);
    } catch (error) {
      console.error('Error deleting node:', error);
      setError(error.response?.data?.message || 'Failed to delete node');
    }
  };

  const handleToggleStatus = async (id) => {
    const node = nodes.find((n) => n.id === id);
    setError('');
    try {
      await api.post(`/admin/nodes/${id}/toggle-status`);
      await loadNodes();
      showSuccess(node ? `Node "${node.name}" ${node.isActive ? 'deactivated' : 'activated'}` : 'Node status updated');
    } catch (error) {
      console.error('Error toggling node status:', error);
      setError(error.response?.data?.message || 'Failed to toggle node status');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      ztimeBasicUrl: '',
      datasyncBasicUrl: '',
      lisabackendBasicUrl: '',
      loginUsername: '',
      loginPassword: '',
    });
    setShowPassword(false);
  };

  const startEdit = (node) => {
    setEditingNode(node);
    setError('');
    setFormData({
      name: node.name,
      ztimeBasicUrl: node.ztimeBasicUrl,
      datasyncBasicUrl: node.datasyncBasicUrl,
      lisabackendBasicUrl: node.lisabackendBasicUrl,
      loginUsername: node.loginUsername,
      loginPassword: node.loginPassword,
    });
    setShowCreateForm(true);
  };

  const cancelEdit = () => {
    setEditingNode(null);
    setShowCreateForm(false);
    setError('');
    resetForm();
  };

  if (loading) return <AdminLoading message="Loading nodes..." />;

  return (
    <div className="space-y-6">
      {toastElement}
      {confirmDialog}
      <AdminPageHeader
        icon={Server}
        title="Node Management"
        subtitle="Manage integration node configurations"
        rightContent={
          !showCreateForm && (
            <AdminAddButton
              onClick={() => {
                setShowCreateForm(true);
                setEditingNode(null);
                setError('');
                resetForm();
              }}
              label="Add Node"
            />
          )
        }
      />

      {error && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 shadow-sm">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900 flex items-center">
              {editingNode ? (
                <>
                  <Edit className="h-6 w-6 mr-2 text-purple-600" />
                  Edit Node
                </>
              ) : (
                <>
                  <Plus className="h-6 w-6 mr-2 text-purple-600" />
                  Create New Node
                </>
              )}
            </h3>
            <button
              onClick={cancelEdit}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={editingNode ? handleUpdateNode : handleCreateNode} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Node Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  placeholder="e.g., node1"
                  required
                  disabled={submitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Login Username</label>
                <input
                  type="text"
                  value={formData.loginUsername}
                  onChange={(e) => setFormData({ ...formData, loginUsername: e.target.value })}
                  className="mt-1 block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  placeholder="Username for authentication"
                  required
                  disabled={submitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Login Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.loginPassword}
                    onChange={(e) => setFormData({ ...formData, loginPassword: e.target.value })}
                    className="mt-1 block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 sm:text-sm pr-10"
                    placeholder="Password for authentication"
                    required
                    disabled={submitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 mt-1"
                  >
                    {showPassword ? <XCircle className="h-5 w-5" /> : <Check className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                <Globe className="h-4 w-4 mr-2" />
                Integration URLs
              </h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ztime Basic URL</label>
                  <input
                    type="url"
                    value={formData.ztimeBasicUrl}
                    onChange={(e) => setFormData({ ...formData, ztimeBasicUrl: e.target.value })}
                    className="mt-1 block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    placeholder="https://ztime.example.com"
                    required
                    disabled={submitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Datasync Basic URL</label>
                  <input
                    type="url"
                    value={formData.datasyncBasicUrl}
                    onChange={(e) => setFormData({ ...formData, datasyncBasicUrl: e.target.value })}
                    className="mt-1 block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    placeholder="https://datasync.example.com"
                    required
                    disabled={submitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">LisaBackend Basic URL</label>
                  <input
                    type="url"
                    value={formData.lisabackendBasicUrl}
                    onChange={(e) => setFormData({ ...formData, lisabackendBasicUrl: e.target.value })}
                    className="mt-1 block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    placeholder="https://lisabackend.example.com"
                    required
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={cancelEdit}
                disabled={submitting}
                className="px-5 py-2.5 border border-gray-300 rounded-xl shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="h-4 w-4 inline mr-2" />
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2.5 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {editingNode ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 inline mr-2" />
                    {editingNode ? 'Update Node' : 'Create Node'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Nodes Table */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Node Name</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Ztime URL</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Datasync URL</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">LisaBackend URL</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Username</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {nodes.map((node) => (
                <tr key={node.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Server className="h-5 w-5 text-purple-500 mr-2" />
                      <span className="text-sm font-medium text-gray-900">{node.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Globe className="h-4 w-4 text-blue-500 mr-2" />
                      <span className="text-sm text-gray-600 truncate max-w-xs">{node.ztimeBasicUrl}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Globe className="h-4 w-4 text-green-500 mr-2" />
                      <span className="text-sm text-gray-600 truncate max-w-xs">{node.datasyncBasicUrl}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Globe className="h-4 w-4 text-indigo-500 mr-2" />
                      <span className="text-sm text-gray-600 truncate max-w-xs">{node.lisabackendBasicUrl}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Lock className="h-4 w-4 text-gray-500 mr-2" />
                      <span className="text-sm text-gray-600">{node.loginUsername}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      node.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {node.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <AdminRowActions align="start">
                      <AdminActionButton variant="edit" onClick={() => startEdit(node)} />
                      <AdminActionButton variant={node.isActive ? 'deactivate' : 'activate'} onClick={() => handleToggleStatus(node.id)} />
                      <AdminActionButton variant="delete" onClick={() => handleDeleteNode(node.id)} />
                    </AdminRowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {nodes.length === 0 && (
          <div className="text-center py-12">
            <Server className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No nodes configured yet</p>
            <p className="text-sm text-gray-400 mt-1">Create a node to get started with office mapping</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NodeManagement;
